import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignIn = () => {
  const navigate = useNavigate();
  const { signIn, resetPassword } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e?.target?.name]: e?.target?.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      if (error) {
        setError(error?.message || 'Failed to sign in. Please check your credentials.');
        setLoading(false);
        return;
      }
      if (data?.user) {
        navigate('/user-dashboard');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setForgotError('');
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    try {
      const { error } = await resetPassword(forgotEmail);
      if (error) {
        setForgotError(error?.message || 'Failed to send reset email. Please try again.');
      } else {
        setForgotSuccess(true);
      }
    } catch (err) {
      setForgotError('An unexpected error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess(false);
  };

  return (
    <>
      <Helmet>
        <title>Sign In - PowerPlay</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex justify-center">
              <Icon name="Trophy" className="h-12 w-12 text-primary-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              {showForgotPassword ? 'Reset your password' : 'Sign in to your account'}
            </h2>
            {!showForgotPassword && (
              <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                Or{' '}
                <Link to="/signup" className="font-medium text-primary hover:text-primary/80 underline underline-offset-2">
                  create a new account
                </Link>
              </p>
            )}
          </div>

          {/* ── Forgot Password Panel ── */}
          {showForgotPassword ? (
            <div className="mt-8 space-y-6">
              {forgotSuccess ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-5 text-center space-y-3">
                  <Icon name="MailCheck" className="h-10 w-10 text-green-600 dark:text-green-400 mx-auto" />
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Password reset email sent!
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    Check your inbox at <span className="font-semibold">{forgotEmail}</span> and follow the link to reset your password.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enter the email address associated with your account and we'll send you a link to reset your password.
                  </p>

                  {forgotError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className="flex items-center">
                        <Icon name="AlertCircle" className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
                        <p className="text-sm text-red-800 dark:text-red-300">{forgotError}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email address
                    </label>
                    <Input
                      id="forgot-email"
                      name="forgot-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={forgotEmail}
                      onChange={(e) => { setForgotEmail(e?.target?.value); setForgotError(''); }}
                      placeholder="Enter your email"
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex justify-center items-center"
                  >
                    {forgotLoading ? (
                      <>
                        <Icon name="Loader2" className="animate-spin h-5 w-5 mr-2" />
                        Sending...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </Button>
                </form>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80"
                >
                  <Icon name="ArrowLeft" className="h-4 w-4 mr-1" />
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
            /* ── Sign In Form ── */
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <Icon name="AlertCircle" className="h-5 w-5 text-red-600 dark:text-red-400 mr-3" />
                    <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email address
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData?.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center"
                >
                  {loading ? (
                    <>
                      <Icon name="Loader2" className="animate-spin h-5 w-5 mr-2" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default SignIn;